import {
  Button,
  Pagination,
  SegmentedControl,
  Skeleton,
  Stack,
} from '@mantine/core';
import { ListHead } from './Header/ListHead';
import { type Content, ListContent } from './Content/ListContent';
import { toNumber } from 'lodash';
import { PageHeader } from '../PageHeader/PageHeader';
import React from 'react';

interface PerPageSelector {
  value: number;
  setter: (n: number) => void;
  label: string;
}

interface LazyLoad {
  onLoad: () => void;
  disabled: boolean;
}

interface PaginationLoad {
  pageTotal: number;
  page: number;
  setPage: (page: number) => void;
}

interface Props<T> {
  name?: string;
  selectors?: JSX.Element[];
  columns: JSX.Element[];
  content: Content<T>;
  controls?: JSX.Element[];
  perPageSelector?: PerPageSelector;
  perPageSelectorData?: string[];
  loading?: boolean;
  pagination?: PaginationLoad;
  lazyLoad?: LazyLoad;
}

export const List = <T,>(props: Props<T>): JSX.Element => {
  const {
    name,
    selectors,
    columns,
    content,
    perPageSelector,
    perPageSelectorData = ['5', '10', '25', '50', '100'],
    loading = false,
    pagination,
    lazyLoad,
  } = props;

  if (columns.length !== content.renders.length) {
    throw new Error('Columns and column renders length mismatch');
  }

  return (
    <Stack className="h-full flex flex-col">
      {name && <PageHeader name={name} controls={props.controls} />}
      {selectors && (
        <Skeleton visible={loading}>
          <div className="justify-center border rounded-card p-4">
            <div className="flex flex-row gap-3 items-end">
              {React.Children.toArray(selectors).map(el => (
                <div
                  key={(el as React.ReactElement).key}
                  className="w-full"
                >
                  {el}
                </div>
              ))}
            </div>
          </div>
        </Skeleton>
      )}
      {loading ? (
        <Skeleton className="flex-grow" />
      ) : (
        <div className="flex-1 overflow-auto flex flex-col rounded-card border px-4 pt-4">
          <table className="table-auto">
            <thead
              className="sticky top-0 bg-white z-50"
              style={{ boxShadow: '0 -20px 0 0 white' }}
            >
              <ListHead columns={columns} />
            </thead>
            <tbody>
              <ListContent content={content} />
            </tbody>
          </table>
          <div className="flex flex-row flex-grow" />
          {lazyLoad ? (
            <div className="flex flex-col w-full pb-3 justify-center">
              <Button
                variant="subtle"
                onClick={lazyLoad.onLoad}
                disabled={lazyLoad.disabled}
              >
                Načíst další
              </Button>
            </div>
          ) : (
            <div className="flex flex-col justify-evenly items-center sticky bottom-0 py-4 bg-white z-50 lg:flex-row">
              {perPageSelector && (
                <div className="flex gap-2 items-center">
                  <div>{perPageSelector.label}</div>
                  <SegmentedControl
                    data={perPageSelectorData}
                    onChange={value => perPageSelector.setter(toNumber(value))}
                    value={perPageSelector.value.toString()}
                  />
                </div>
              )}
              {pagination && pagination.pageTotal > 1 && (
                <Pagination
                  style={{ boxShadow: '0 20px 0 0 white' }}
                  total={pagination.pageTotal}
                  value={pagination.page}
                  onChange={pagination.setPage}
                />
              )}
            </div>
          )}
        </div>
      )}
    </Stack>
  );
};
